"use client";

import { COMPANY_VALUES } from "@/constants/about.constants";
import { useValuesTabs } from "@/hooks/useValuesTabs";

export default function ValuesTabs() {
  const { active, setActive, openMobile, toggleMobile, current } = useValuesTabs();

  return (
    <>
      <div className="mx-auto hidden w-full max-w-275 flex-row items-center gap-12 md:flex">
        <div className="flex w-65 shrink-0 flex-col gap-3">
          {COMPANY_VALUES.map((v, i) => {
            const isActive = i === active;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setActive(i)}
                aria-pressed={isActive}
                className={[
                  "select-none rounded-2xl px-6 py-[18px] text-left text-[1.05rem] font-medium tracking-[0.01em] transition-all duration-200",
                  isActive
                    ? "bg-[#00d8ff] font-bold text-[#05063a] shadow-[0_0_28px_rgba(0,216,255,0.55),0_4px_16px_rgba(0,216,255,0.3)]"
                    : "border-[1.5px] border-[rgba(80,100,200,0.25)] bg-[rgba(15,20,60,0.85)] text-[#e0e0f0] hover:border-[rgba(0,200,255,0.35)] hover:bg-[rgba(30,40,90,0.9)]",
                ].join(" ")}
                style={isActive ? undefined : { borderWidth: 1.5 }}
              >
                {v.title}
              </button>
            );
          })}
        </div>

        <div className="relative h-[380px] flex-1">
          <div
            aria-hidden
            className="absolute -left-2 -right-2 top-0 h-[290px] rounded-[22px] border backdrop-blur-md"
            style={{
              background: "rgba(255,255,255,0.028)",
              borderColor: "rgba(255,255,255,0.07)",
              zIndex: 1,
            }}
          />
          <div
            aria-hidden
            className="absolute -left-1 -right-1 top-[18px] h-[290px] rounded-[20px] border backdrop-blur-md"
            style={{
              background: "rgba(255,255,255,0.05)",
              borderColor: "rgba(255,255,255,0.09)",
              zIndex: 2,
            }}
          />
          <div
            aria-hidden
            className="absolute -left-1 -right-1 bottom-[18px] h-[290px] rounded-[20px] border backdrop-blur-md"
            style={{
              background: "rgba(255,255,255,0.05)",
              borderColor: "rgba(255,255,255,0.09)",
              zIndex: 2,
            }}
          />
          <div
            aria-hidden
            className="absolute -left-2 -right-2 bottom-0 h-[290px] rounded-[22px] border backdrop-blur-md"
            style={{
              background: "rgba(255,255,255,0.028)",
              borderColor: "rgba(255,255,255,0.07)",
              zIndex: 1,
            }}
          />

          <div
            className="absolute inset-x-0 bottom-9 top-9 flex items-center justify-center rounded-[20px] border px-[52px] py-[50px] backdrop-blur-md"
            style={{
              background: "rgba(255,255,255,0.09)",
              borderColor: "rgba(255,255,255,0.18)",
              boxShadow: "inset 0 0 40px rgba(255,255,255,0.03)",
              zIndex: 5,
            }}
          >
            <div
              key={current.id}
              className="w-full text-center"
              style={{ animation: "fadeUp 0.32s ease" }}
            >
              <p className="text-[1.05rem] font-normal leading-[1.95] tracking-[0.015em] text-white">
                {current.body}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[600px] flex-col gap-3 md:hidden">
        {COMPANY_VALUES.map((v, i) => {
          const isOpen = openMobile === i;
          return (
            <div
              key={v.id}
              className={[
                "overflow-hidden rounded-[14px] border-[1.5px] transition-[border-color,box-shadow] duration-200",
                isOpen
                  ? "border-[#00d8ff] shadow-[0_0_20px_rgba(0,216,255,0.2)]"
                  : "border-[rgba(80,100,200,0.25)]",
              ].join(" ")}
              style={{ background: "rgba(15,20,60,0.85)" }}
            >
              <button
                type="button"
                onClick={() => toggleMobile(i)}
                aria-expanded={isOpen}
                className="flex w-full select-none cursor-pointer items-center justify-between gap-3 px-5 py-[18px] text-left"
              >
                <span
                  className={[
                    "text-base font-semibold transition-colors duration-200",
                    isOpen ? "text-[#00d8ff]" : "text-[#e0e0f0]",
                  ].join(" ")}
                >
                  {v.title}
                </span>
                <span
                  aria-hidden
                  className={[
                    "flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full border transition-all duration-300",
                    isOpen
                      ? "rotate-180 border-[#00d8ff] bg-[#00d8ff]"
                      : "border-white/15 bg-white/[0.08]",
                  ].join(" ")}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-3 w-3"
                    style={{
                      fill: "none",
                      strokeWidth: 2.5,
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      stroke: isOpen ? "#05063a" : "#fff",
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </button>

              <div
                className={[
                  "overflow-hidden transition-[max-height,padding] duration-300 ease-out",
                  isOpen ? "max-h-[300px]" : "max-h-0",
                ].join(" ")}
                style={{ padding: isOpen ? "0 20px 20px" : "0 20px" }}
              >
                <p
                  className="border-t pt-[14px] text-[0.95rem] font-normal leading-[1.85] text-white/88"
                  style={{ borderColor: "rgba(255,255,255,0.08)" }}
                >
                  {v.body}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </>
  );
}
